/**
 * UAT-MEDICAL-ACCESS-REQUEST-01 — Provider-side "Request access".
 *
 * A Provider user can NEVER forward a medical file or grant a colleague
 * access. This control only ASKS the Patient to grant a SPECIFIC eligible
 * person. Targets come from the journey's canonical relationships, so an
 * unassigned Doctor is simply not offered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldQuestion, Clock, Check, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';

const PURPOSES = [
  'treatment_evaluation',
  'medical_history',
  'pre_treatment_review',
  'post_treatment_followup',
  'other',
];

export const MedicalAccessRequestPanel = ({ threadId, attachmentId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [purpose, setPurpose] = useState('treatment_evaluation');
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!threadId || !attachmentId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(
        `/clinic/threads/${encodeURIComponent(threadId)}/access-request-targets`,
        { params: { attachment_id: attachmentId } },
      );
      setTargets(r?.data?.targets || []);
    } catch (err) {
      setError(err?.response?.data?.message || t('provider.messaging.accessRequest.loadFailed'));
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, [threadId, attachmentId, t]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const request = async (target) => {
    setBusy(`${target.user_id}::${target.role}`);
    try {
      await api.post('/clinic/medical-access-requests', {
        attachment_id: attachmentId,
        thread_id: threadId,
        target_user_id: target.user_id,
        target_role: target.role,
        purpose,
      });
      toast({
        title: t('provider.messaging.accessRequest.sentTitle'),
        description: t('provider.messaging.accessRequest.sentBody', { name: target.name || target.user_id }),
      });
      load();
    } catch (err) {
      toast({
        title: t('provider.messaging.toast.errorTitle'),
        description: err?.response?.data?.message || t('provider.messaging.accessRequest.sendFailed'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  if (!threadId || !attachmentId) return null;

  return (
    <div className="border-t px-4 py-2.5 bg-slate-50" data-testid="medical-access-request-panel">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="text-xs font-medium text-blue-700 hover:underline flex items-center gap-1.5"
          data-testid="request-access-open-btn"
        >
          <ShieldQuestion className="h-3.5 w-3.5" />
          {t('provider.messaging.accessRequest.cta')}
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] leading-snug text-slate-500" data-testid="request-access-explainer">
            {t('provider.messaging.accessRequest.explainer')}
          </p>
          <label className="block text-[11px] text-slate-600">
            {t('provider.messaging.accessRequest.purposeLabel')}
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1 text-xs bg-white"
              data-testid="request-access-purpose-select"
            >
              {PURPOSES.map((p) => (
                <option key={p} value={p}>{t(`provider.messaging.accessRequest.purpose.${p}`)}</option>
              ))}
            </select>
          </label>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {error && (
            <p className="text-[11px] text-red-600" data-testid="request-access-error">{error}</p>
          )}
          {!loading && !error && targets.length === 0 && (
            <p className="text-[11px] text-slate-500" data-testid="request-access-empty">
              {t('provider.messaging.accessRequest.noTargets')}
            </p>
          )}
          {targets.filter((x) => !x.is_self).map((x) => {
            const key = `${x.user_id}::${x.role}`;
            return (
              <div
                key={key}
                className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1.5"
                data-testid={`request-access-target-${x.user_id}-${x.role}`}
              >
                <span className="truncate text-slate-800">{x.name || x.user_id}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  {t(`patient.medicalFiles.recipientRoles.${x.role}`) !== `patient.medicalFiles.recipientRoles.${x.role}`
                    ? t(`patient.medicalFiles.recipientRoles.${x.role}`)
                    : x.role}
                </span>
                <span className="ml-auto">
                  {x.has_grant ? (
                    <span className="text-[10px] text-emerald-700 flex items-center gap-1" data-testid={`request-access-state-approved-${x.user_id}`}>
                      <Check className="h-3 w-3" /> {t('provider.messaging.accessRequest.stateApproved')}
                    </span>
                  ) : x.pending_request_id ? (
                    <span className="text-[10px] text-amber-700 flex items-center gap-1" data-testid={`request-access-state-pending-${x.user_id}`}>
                      <Clock className="h-3 w-3" /> {t('provider.messaging.accessRequest.statePending')}
                    </span>
                  ) : (
                    <button
                      onClick={() => request(x)}
                      disabled={busy === key}
                      className="text-[11px] font-medium text-blue-700 hover:underline disabled:opacity-50"
                      data-testid={`request-access-btn-${x.user_id}-${x.role}`}
                    >
                      {busy === key ? '…' : t('provider.messaging.accessRequest.requestBtn')}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MedicalAccessRequestPanel;
